import React, { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useNavigate, useParams } from 'react-router-dom';

import { AppButton } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';

import {
  useMainPatientDmInfo,
  useMainPatientDmInfoMutation,
  type DmInformation,
} from '@/hooks/main/useMainPatientDmInfo';

import PatientDmInfoSection from '@pages/main/patient/components/PatientDmInfoSection';
import PatientNavigationButtons from '@pages/main/patient/components/PatientNavigationButtons';
import useAppToast from '@/hooks/useAppToast';
import { NotFound } from '@/pages/main/system/NotFound';
import {
  emptyDmInfoForm,
  patientDmInfoFormSchema,
  type DmInfoFormData,
} from '@/schemas/patientDmInfoFormSchema';

const toRadioValue = (v: boolean | null | undefined): '1' | '0' | '' => {
  if (v === true) return '1';
  if (v === false) return '0';
  return '';
};

const PatientDmInfoEditPage: React.FC = () => {
  const { tenant, id } = useParams<{ tenant: string; id: string }>();
  const navigate = useNavigate();
  const sidebarOpen = useSidebarOpen();
  const toast = useAppToast();

  const patientId = useMemo(() => (id ? Number(id) : undefined), [id]);

  const { data, isLoading, isError } = useMainPatientDmInfo(patientId);
  const mutation = useMainPatientDmInfoMutation(patientId ?? 0);

  const formMethods = useTrimmedForm<DmInfoFormData>({
    resolver: zodResolver(patientDmInfoFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: emptyDmInfoForm,
  });

  const { reset, handleSubmit, formState } = formMethods;

  useEffect(() => {
    const dm = data?.dm_info;

    if (!dm) {
      reset(emptyDmInfoForm);
      return;
    }

    reset({
      has_spouse: toRadioValue(dm.has_spouse),
      has_children: toRadioValue(dm.has_children),
      children_num: dm.children_num != null ? String(dm.children_num) : '',
      dm_occupation_type_id:
        dm.dm_occupation_type_id != null ? String(dm.dm_occupation_type_id) : '',
      company_name: dm.company_name ?? '',
      mobilephone: dm.mobilephone ?? '',
      email: dm.email ?? '',
      dm_reason_type_id: dm.dm_reason_type_id != null ? String(dm.dm_reason_type_id) : '',
      is_enable_dm: toRadioValue(dm.is_enable_dm),
      free01: dm.free01 ?? '',
      free02: dm.free02 ?? '',
      free03: dm.free03 ?? '',
      free04: dm.free04 ?? '',
      free05: dm.free05 ?? '',
      free06: dm.free06 ?? '',
      free07: dm.free07 ?? '',
      free08: dm.free08 ?? '',
      free09: dm.free09 ?? '',
      free10: dm.free10 ?? '',
      remarks: dm.remarks ?? '',
    });
  }, [data?.dm_info, reset]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!formState.isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };

    if (formState.isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formState.isDirty]);

  const buildPayload = (form: DmInfoFormData): Partial<DmInformation> => {
    const toBoolOrNull = (v: '1' | '0' | '') => (v === '' ? null : v === '1');
    const childrenNumDisabled = form.has_children === '0';

    return {
      has_spouse: toBoolOrNull(form.has_spouse),
      has_children: toBoolOrNull(form.has_children),
      children_num:
        childrenNumDisabled || form.children_num === '' ? null : Number(form.children_num),
      dm_occupation_type_id: form.dm_occupation_type_id ? Number(form.dm_occupation_type_id) : null,
      company_name: form.company_name || null,
      mobilephone: form.mobilephone || null,
      email: form.email || null,
      dm_reason_type_id: form.dm_reason_type_id ? Number(form.dm_reason_type_id) : null,
      is_enable_dm: toBoolOrNull(form.is_enable_dm),
      free01: form.free01 || null,
      free02: form.free02 || null,
      free03: form.free03 || null,
      free04: form.free04 || null,
      free05: form.free05 || null,
      free06: form.free06 || null,
      free07: form.free07 || null,
      free08: form.free08 || null,
      free09: form.free09 || null,
      free10: form.free10 || null,
      remarks: form.remarks || null,
    };
  };

  const onValid = async (formData: DmInfoFormData) => {
    if (!patientId) return;

    try {
      await mutation.mutateAsync(buildPayload(formData));
      toast.success('更新が正常に終了しました。');
      reset(formData);
    } catch {
      toast.error('登録に失敗しました。');
    }
  };

  const onInvalid = () => undefined;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  if (isError || !data) {
    return <NotFound />;
  }

  return (
    <div className="pb-24 patient-dm-info-edit-page">
      <div className="flex items-center justify-end mb-4">
        {tenant && patientId && (
          <PatientNavigationButtons
            id={String(patientId)}
            tenant={tenant}
            navigate={navigate}
            activeKey={null}
            pdfTemplateMenu="full"
          />
        )}
      </div>

      <FormProvider {...formMethods}>
        <div className="space-y-4 pb-28">
          <PatientDmInfoSection
            occupationTypes={data.master.occupation_types}
            reasonTypes={data.master.reason_types}
          />
        </div>
      </FormProvider>

      <BottomBar
        variant="action"
        visible
        right={
          <AppButton
            type="primary"
            className="rounded"
            onClick={() => void handleSubmit(onValid, onInvalid)()}
            loading={mutation.isPending}
          >
            登録
          </AppButton>
        }
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
      />
    </div>
  );
};

export default PatientDmInfoEditPage;
